import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
// local components
import { Row, Col } from '../../../../foundations/Grid';
import AssistiveText from '../../../../components/AssistiveText';
// styles
import {
  ContainerStyled,
  LinkStyled,
  LiStyled,
  NavStyled,
  OlStyled,
  SpanStyledSecondToLastItem,
} from './styles';

const BaseListItemWrapper = ({
  children,
  config,
  index,
}) => {
  return (
    <LiStyled
      isSecondToLastItem={index < config.length - 2}
      className='nmx-nav-link-breadcrumb-container'
      role='none'
      itemProp='itemListElement'
      itemScope
      itemType='https://schema.org/ListItem'>
      {children}
    </LiStyled>
  );
};

const BaseLinkWrapper = ({
  ariaCurrent,
  breadcrumb,
  children,
}) => {
  return (
    <LinkStyled
      role='menuitem'
      linkUrl={breadcrumb.linkUrl}
      className='nmx-nav-link nmx-nav-link-breadcrumb'
      ariaLabel={breadcrumb.linkAriaLabel}
      itemProp='item'
      {...(ariaCurrent && { 'aria-current': 'page' })}>
      {children}
    </LinkStyled>
  );
};

const MetaPosition = ({ index }) => {
  return (
    <meta
      itemProp='position'
      content={index + 1}
    />
  );
};

export const BreadcrumbsNavigationComponent = ({
  config,
  className,
  ...rest
}) => {
  const theme = useContext(ThemeContext);
  const smallDown = theme.smallDown();
  const [isXSmall, setIsXSmall] = useState(smallDown);

  useEffect(() => {
    if (smallDown !== isXSmall) {
      setIsXSmall(smallDown);
    }
  }, [smallDown]);

  return (
    <ContainerStyled
      id='nmx-breadcrumbs-container'
      className={className}
      {...rest}>
      <Row>
        <Col>
          <NavStyled
            id='nmx-breadcrumbs'
            aria-label='Breadcrumbs Navigation'
            className='nmx-breadcrumbs'
          >
            <AssistiveText component='h6'>Breadcrumbs Navigation</AssistiveText>
            <OlStyled
              role='menu'
              className='reduced'
              itemScope
              itemType='https://schema.org/BreadcrumbList'>
              {config.map((breadcrumb, index) => (
                <React.Fragment key={`key-${index + 1}`}>
                  {/* Always display last breadcrumb item */}
                  {index === config.length - 1 && (
                    <BaseListItemWrapper
                      config={config}
                      index={index}>
                      <BaseLinkWrapper
                        ariaCurrent
                        breadcrumb={breadcrumb}>
                        <span itemProp='name'>
                          {breadcrumb.pageName}
                        </span>
                      </BaseLinkWrapper>
                      <MetaPosition index={index} />
                    </BaseListItemWrapper>
                  )
                  }
                  {/* Display ellipses for second last item on mobile, full menu item on desktop */}
                  {!isXSmall && index === config.length - 2 && (
                    <BaseListItemWrapper
                      config={config}
                      index={index}>
                      <BaseLinkWrapper breadcrumb={breadcrumb}>
                        <span itemProp='name'>
                          {breadcrumb.pageName}
                        </span>
                      </BaseLinkWrapper>
                      <MetaPosition index={index} />
                    </BaseListItemWrapper>
                  )}
                  {isXSmall && index === config.length - 2 && (
                    <BaseListItemWrapper
                      config={config}
                      index={index}>
                      <BaseLinkWrapper breadcrumb={breadcrumb}>
                        <meta
                          itemProp='name'
                          content={breadcrumb.pageName}
                        />
                        <SpanStyledSecondToLastItem>
                          &#8230;
                        </SpanStyledSecondToLastItem>
                      </BaseLinkWrapper>
                      <MetaPosition index={index} />
                    </BaseListItemWrapper>
                  )}
                  {/* Display meta (hidden) for all other earlier levels on mobile, full menu item on desktop */}
                  {!isXSmall && index < config.length - 2 && (
                    <BaseListItemWrapper
                      config={config}
                      index={index}>
                      <BaseLinkWrapper breadcrumb={breadcrumb}>
                        <span itemProp='name'>
                          {breadcrumb.pageName}
                        </span>
                      </BaseLinkWrapper>
                      <MetaPosition index={index} />
                    </BaseListItemWrapper>
                  )}
                  {isXSmall && index < config.length - 2 && (
                    <BaseListItemWrapper
                      config={config}
                      index={index}>
                      <link
                        itemProp='item'
                        href={breadcrumb.linkUrl}
                      />
                      <meta
                        itemProp='name'
                        content={breadcrumb.pageName}
                      />
                      <MetaPosition index={index} />
                    </BaseListItemWrapper>
                  )}
                </React.Fragment>
              ))}
            </OlStyled>
          </NavStyled>
        </Col>
      </Row>
    </ContainerStyled>
  );
};

BreadcrumbsNavigationComponent.propTypes = {
  config: PropTypes.any,
  showInformationalBanner: PropTypes.bool,
};

export default BreadcrumbsNavigationComponent;
